import React from 'react';
import clsx from 'clsx';

import { Logo } from './Logo';
import { nonNullable } from '../../utilities';
import { PermissionCheck } from '../../shared';
import { isAppRouteGroupType } from '../../utilities';
import { isAppRouteSingleType } from '../../utilities';
import { Collapser, NavLink, Icon } from '../../components';

type MenuProps = {
  items: AppRouteType[];
};

const needAddSingleRouteToMenu = (route: AppRouteSingleType) => {
  return !!route.url && !!route.menuTitle;
};

const GroupWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <div className="py-xl border-t border-active w-full">{children}</div>;
};

const ItemWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="py-sm w-full">{children}</div>;
};

const AppRoute: React.FC<{
  route: AppRouteSingleType;
  withWrapper: boolean;
}> = ({ route, withWrapper }) => {
  if (!needAddSingleRouteToMenu(route)) return null;

  const { icon } = route;
  const element = (
    <NavLink
      to={route.url!}
      exact={route.url === '/'}
      className={isActive =>
        clsx(
          'transition-default',
          'font-bold text-small text-text rounded-l-xs p-sm block',
          isActive ? 'sp-active-link' : 'sp-hover-link'
        )
      }
    >
      {!!icon && (
        <span className="mr-md">
          {typeof icon === 'string' ? (
            <Icon width icon={icon as IconType} />
          ) : (
            icon
          )}
        </span>
      )}
      {route.menuTitle}
    </NavLink>
  );

  return (
    <PermissionCheck action={route.action || []} type="every">
      {withWrapper ? <ItemWrapper>{element}</ItemWrapper> : element}
    </PermissionCheck>
  );
};

const AppRouteGroup: React.FC<{ route: AppRouteGroupType }> = ({ route }) => {
  const childrenActions = route.routes
    .map(r => r.action)
    .filter(nonNullable)
    .flatMap(action => action);

  return (
    <PermissionCheck action={route.action || []} type="every">
      <PermissionCheck action={childrenActions} type="some">
        <GroupWrapper>
          <Collapser header={route.groupTitle}>
            <div className="flex flex-col mt-sm gap-y-sm max-h-[25rem] overflow-y-auto -ml-md pl-md">
              {route.routes.map((item, index) => (
                <AppRoute key={index} route={item} withWrapper={false} />
              ))}
            </div>
          </Collapser>
        </GroupWrapper>
      </PermissionCheck>
    </PermissionCheck>
  );
};

export const Menu: React.FC<MenuProps> = ({ items }) => {
  return (
    <div className="flex flex-col items-start justify-center p-xl pr-0">
      <Logo />
      {items
        .map(item => {
          if (isAppRouteSingleType(item) && needAddSingleRouteToMenu(item)) {
            return <AppRoute route={item} withWrapper />;
          }
          if (isAppRouteGroupType(item)) return <AppRouteGroup route={item} />;

          return null;
        })
        .filter(nonNullable)
        .map((element, index) => (
          <React.Fragment key={index}>{element}</React.Fragment>
        ))}
    </div>
  );
};
